import * as React from 'react'
import {styled} from '@mui/material/styles'
import MuiDrawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {MID_WIDTH_PAGE_CONTAINER, SIDE_MENU_WIDTH} from 'appearance/styles'
import {getMenuSiteByRole} from 'constants/sideMenu'
import ListItem from './ListItem'
import {useIsRtl} from 'store/Store'
import {useLocation, useNavigate} from 'react-router-dom'
import SelectContent from 'components/Control/SelectContent'
import {Avatar, Box, Typography} from '@mui/material'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import {useSideMenuContext} from 'context/SideMenuContext'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

const CATEGORY_ORDER = ['management', 'billing', 'system']

const SideMenu = ({user, account}) => {
    const isRtl = useIsRtl()
    const {t} = useTranslation()

    const list = useMemo(() => getMenuSiteByRole(user, account), [user, account])

    const {open, setOpen} = useSideMenuContext()
    const navigate = useNavigate()
    const location = useLocation()
    const currentPage = location.pathname.split('/')[1]

    const {uncategorized, categorized} = useMemo(() => {
        const pages = Object.values(list || {})
        const uncategorized = pages.filter((p) => !p.category)
        const grouped = {}
        CATEGORY_ORDER.forEach((cat) => {
            const items = pages.filter((p) => p.category === cat)
            if (items.length > 0) grouped[cat] = items
        })
        return {uncategorized, categorized: grouped}
    }, [list])

    if (!list) return null

    return (
        <Drawer variant="permanent" open={open}>
            <DrawerHeader>
                <IconButton onClick={() => setOpen(false)}>
                    {isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
            </DrawerHeader>
            <Divider />

            {open ? (
                <Box display={'flex'} width={'100%'} justifyContent={'center'} padding={1}>
                    <SelectContent />
                </Box>
            ) : (
                <CollapsedSelectContainer>
                    <CollapsedSelectAvatar>
                        <DevicesRoundedIcon sx={{fontSize: '1rem'}} />
                    </CollapsedSelectAvatar>
                </CollapsedSelectContainer>
            )}
            {/* <CurrentAccount user={user} account={account} /> */}

            <Divider />
            <List>
                {uncategorized.map((page, index) => (
                    <ListItem
                        selected={currentPage === page?.text}
                        key={index}
                        page={page}
                        onClick={() => navigate(page?.navigate)}
                    />
                ))}
                {Object.entries(categorized).map(([category, pages]) => (
                    <React.Fragment key={category}>
                        {open && (
                            <CategoryLabel variant="caption">
                                {t(`side_menu.categories.${category}`)}
                            </CategoryLabel>
                        )}
                        {!open && <Divider sx={{my: 0.5}} />}
                        {pages.map((page, index) => (
                            <ListItem
                                selected={currentPage === page?.text}
                                key={index}
                                page={page}
                                onClick={() => navigate(page?.navigate)}
                            />
                        ))}
                    </React.Fragment>
                ))}
            </List>
        </Drawer>
    )
}

export default SideMenu

const openedMixin = (theme) => ({
    width: SIDE_MENU_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
})

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(0)} + 1px)`,
    [theme.breakpoints.up(MID_WIDTH_PAGE_CONTAINER)]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
})

const DrawerHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}))

const CollapsedSelectContainer = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
    paddingInline: theme.spacing(1),
}))

const CategoryLabel = styled(Typography)(({theme}) => ({
    display: 'block',
    paddingInline: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: (theme.vars || theme).palette.text.disabled,
}))

const CollapsedSelectAvatar = styled(Avatar)(({theme}) => ({
    width: 28,
    height: 28,
    backgroundColor: (theme.vars || theme).palette.background.paper,
    color: (theme.vars || theme).palette.text.secondary,
    border: `1px solid ${(theme.vars || theme).palette.divider}`,
}))

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        width: SIDE_MENU_WIDTH,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    })
)
